<template>
  <div class="subscription-view-container">
    <MainHeader :title="'Subscription Audit'">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <!-- Intro -->
      <!-- <div class="intro-card">
        <div class="intro-icon subscription-icon">
          <font-awesome-icon :icon="['fas', 'arrows-rotate']" />
        </div>
        <p class="intro-text">
          Track your recurring charges, spot hidden subscriptions, and see what
          they really cost you every year.
        </p>
      </div> -->

      <!-- Annual Summary (always visible once subscriptions exist) -->
      <div v-if="subscriptions.length > 0" class="ud-section">
        <span class="section-label">Annual Summary</span>
        <div class="summary-grid">
          <div class="wrapper-card summary-card">
            <span class="summary-label">Per Year</span>
            <span class="summary-value"
              >${{ formatNumber(totalYearlyCost) }}</span
            >
          </div>
          <div class="wrapper-card summary-card">
            <span class="summary-label">Per Month</span>
            <span class="summary-value"
              >${{ formatNumber(totalMonthlyCost) }}</span
            >
          </div>
          <div class="wrapper-card summary-card">
            <span class="summary-label">Active</span>
            <span class="summary-value">{{ activeCount }}</span>
          </div>
          <div class="wrapper-card summary-card">
            <span class="summary-label">Categories</span>
            <span class="summary-value">{{ categoryBreakdown.length }}</span>
          </div>
        </div>
      </div>

      <!-- Scan Transactions Section -->
      <div v-if="hasTransactions" class="ud-section">
        <div class="section-header-row">
          <span class="section-label">Smart Scan</span>
          <button
            v-if="!scanning"
            type="button"
            class="link-btn"
            @click="runScan"
          >
            {{ detectedSubscriptions.length > 0 ? 'Re-scan' : 'Scan now' }}
          </button>
        </div>

        <div v-if="scanning" class="wrapper-card scanning-card">
          <font-awesome-icon
            :icon="['fas', 'spinner']"
            spin
            class="scanning-icon"
          />
          <p class="scanning-text">
            Analyzing your transactions for recurring charges...
          </p>
        </div>

        <!-- Detected suggestions -->
        <div
          v-if="detectedSubscriptions.length > 0 && !scanning"
          class="detected-list"
        >
          <p class="detected-count">
            Found {{ detectedSubscriptions.length }} potential subscription{{
              detectedSubscriptions.length !== 1 ? 's' : ''
            }}
          </p>
          <div
            v-for="(detected, idx) in detectedSubscriptions"
            :key="idx"
            class="wrapper-card suggestion-card"
          >
            <div class="suggestion-header">
              <div class="suggestion-icon-wrap">
                <font-awesome-icon :icon="['fas', 'magnifying-glass-chart']" />
              </div>
              <div class="suggestion-info">
                <p class="suggestion-name">{{ detected.name }}</p>
                <p class="suggestion-meta">
                  {{ detected.frequency }} &middot;
                  {{ detected.occurrences }} transactions &middot;
                  {{ detected.confidence }}% match
                </p>
              </div>
              <div class="suggestion-actions">
                <button
                  type="button"
                  class="icon-btn"
                  @click="acceptDetected(detected)"
                  aria-label="Add"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                </button>
                <button
                  type="button"
                  class="icon-btn icon-btn-danger"
                  @click="rejectDetected(idx)"
                  aria-label="Ignore"
                >
                  <font-awesome-icon :icon="['fas', 'xmark']" />
                </button>
              </div>
            </div>
            <div class="suggestion-amount-row">
              <span class="suggestion-avg"
                >~${{ formatNumber(detected.amount) }}/{{
                  detected.frequency === 'monthly'
                    ? 'mo'
                    : detected.frequency === 'yearly'
                      ? 'yr'
                      : 'period'
                }}</span
              >
              <span class="suggestion-yearly"
                >${{ formatNumber(detected.estimatedYearly) }}/year</span
              >
            </div>
            <div class="confidence-bar">
              <div
                class="confidence-fill"
                :style="{
                  width: detected.confidence + '%',
                  background: confidenceColor(detected.confidence),
                }"
              />
            </div>
          </div>
        </div>

        <!-- <div
          v-if="!scanning && detectedSubscriptions.length === 0 && hasScanned"
          class="wrapper-card empty-scan"
        >
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="empty-scan-icon"
          />
          <p class="empty-scan-title">No recurring charges found</p>
          <p class="empty-scan-sub">
            We scanned your transactions but didn't find any clear subscription
            patterns. Try adding them manually.
          </p>
        </div> -->
      </div>

      <!-- No transactions message for free users -->
      <!-- <div v-if="!hasTransactions" class="ud-section">
        <div class="wrapper-card upgrade-card">
          <font-awesome-icon
            :icon="['fas', 'wand-magic-sparkles']"
            class="upgrade-icon"
          />
          <p class="upgrade-title">Smart Scan available for Premium</p>
          <p class="upgrade-sub">
            Connect your bank account to automatically detect subscriptions from
            your transaction history. You can still add subscriptions manually.
          </p>
        </div>
      </div> -->

      <!-- Your Subscriptions -->
      <div class="ud-section">
        <div class="section-header-row">
          <span class="section-label">Your Subscriptions</span>
          <button
            v-if="subscriptions.length > 0 && !addingSubscription"
            type="button"
            class="link-btn"
            @click="startAddSubscription"
          >
            + Add another
          </button>
        </div>

        <!-- Empty state -->
        <div
          v-if="subscriptions.length === 0 && !addingSubscription"
          class="wrapper-card empty-subscriptions"
        >
          <font-awesome-icon
            :icon="['fas', 'arrows-rotate']"
            class="empty-icon"
          />
          <p class="empty-title">No subscriptions tracked yet</p>
          <p class="empty-sub">
            Add your Netflix, gym membership, or any recurring charge to see
            your annual cost.
          </p>
          <button
            type="button"
            class="gradient-button"
            @click="startAddSubscription"
          >
            + Add your first subscription
          </button>
        </div>

        <!-- Subscription cards -->
        <div
          v-for="(sub, idx) in sortedSubscriptions"
          :key="sub.id"
          class="wrapper-card subscription-card"
          :class="{ inactive: !sub.active }"
        >
          <div class="subscription-card-header">
            <div class="subscription-icon-wrap" :style="iconStyle(sub.color)">
              <font-awesome-icon :icon="['fas', sub.icon || 'arrows-rotate']" />
            </div>
            <div class="subscription-info">
              <p class="subscription-name">
                {{ sub.name }}
                <span v-if="!sub.active" class="paused-tag">paused</span>
              </p>
              <p class="subscription-meta">
                {{ categoryLabel(sub.category) }} &middot;
                {{ frequencyLabel(sub.frequency) }}
              </p>
            </div>
            <div class="subscription-actions">
              <button
                type="button"
                class="icon-btn"
                @click="toggleActive(idx)"
                :aria-label="sub.active ? 'Pause' : 'Resume'"
              >
                <font-awesome-icon
                  :icon="['fas', sub.active ? 'pause' : 'play']"
                />
              </button>
              <button
                type="button"
                class="icon-btn"
                @click="editSubscription(idx)"
                aria-label="Edit"
              >
                <font-awesome-icon :icon="['fas', 'pen']" />
              </button>
              <button
                type="button"
                class="icon-btn icon-btn-danger"
                @click="removeSubscription(idx)"
                aria-label="Remove"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
          <div class="subscription-amount-row">
            <span class="subscription-amount-label">{{
              sub.frequency === 'yearly'
                ? 'Yearly'
                : sub.frequency === 'quarterly'
                  ? 'Quarterly'
                  : sub.frequency === 'weekly'
                    ? 'Weekly'
                    : 'Monthly'
            }}</span>
            <span class="subscription-amount-value"
              >${{ formatNumber(sub.amount) }}</span
            >
            <span class="subscription-yearly"
              >${{ formatNumber(yearlyCost(sub)) }}/yr</span
            >
          </div>
        </div>

        <!-- Add/Edit Form -->
        <div
          v-if="addingSubscription"
          class="wrapper-card add-subscription-card"
        >
          <div class="add-subscription-header">
            <span class="add-subscription-title">{{
              editingIndex !== null ? 'Edit Subscription' : 'Add a Subscription'
            }}</span>
            <button
              type="button"
              class="icon-btn"
              @click="cancelAddSubscription"
              aria-label="Cancel"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>

          <div class="ud-field">
            <label class="ud-label">Subscription name</label>
            <input
              v-model="form.name"
              type="text"
              class="input"
              placeholder="e.g. Netflix, Gym membership"
            />
          </div>

          <div class="form-row mt">
            <div class="ud-field flex-1">
              <label class="ud-label">Amount</label>
              <div class="currency-input">
                <span class="currency-symbol">$</span>
                <input
                  v-model.number="form.amount"
                  type="number"
                  class="input currency-field"
                  placeholder="0"
                  inputmode="decimal"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Frequency</label>
              <select
                v-model="form.frequency"
                class="form-select grey-outline-form-select"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>

          <div class="form-row mt">
            <div class="ud-field flex-1">
              <label class="ud-label">Category</label>
              <select
                v-model="form.category"
                class="form-select grey-outline-form-select"
              >
                <option
                  v-for="cat in subscriptionCategories"
                  :key="cat.value"
                  :value="cat.value"
                >
                  {{ cat.label }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="formError" class="form-error">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span>{{ formError }}</span>
          </div>

          <button
            type="button"
            class="gradient-button w-100 mt"
            :disabled="!isFormValid"
            @click="saveSubscription"
          >
            {{ editingIndex !== null ? 'Save Changes' : 'Add Subscription' }}
          </button>
        </div>
      </div>

      <!-- Category Breakdown -->
      <div v-if="categoryBreakdown.length > 0" class="ud-section">
        <span class="section-label">By Category</span>
        <div class="category-list">
          <div
            v-for="cat in categoryBreakdown"
            :key="cat.category"
            class="wrapper-card category-card"
          >
            <div class="category-card-header">
              <div class="category-icon-wrap" :style="iconStyle(cat.color)">
                <font-awesome-icon :icon="['fas', cat.icon]" />
              </div>
              <div class="category-info">
                <p class="category-name">{{ catLabel(cat.category) }}</p>
                <p class="category-meta">
                  {{ cat.count }} subscription{{ cat.count !== 1 ? 's' : '' }}
                </p>
              </div>
              <span class="category-cost"
                >${{ formatNumber(cat.yearlyCost) }}/yr</span
              >
            </div>
            <div class="category-bar-bg">
              <div
                class="category-bar-fill"
                :style="{ width: categoryPercentage(cat.yearlyCost) + '%' }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store';
import MainHeader from '../../components/View-Header.vue';

// ── Types ───────────────────────────────────────────────────────────

interface Subscription {
  id: number;
  name: string;
  amount: number;
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
  category: string;
  source: 'manual' | 'detected';
  active: boolean;
  icon: string;
  color: string;
  nextBillingDate?: string;
}

interface SubscriptionForm {
  name: string;
  amount: number;
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
  category: string;
  active: boolean;
  icon: string;
  color: string;
}

interface Transaction {
  id?: string;
  transactionID?: string;
  name: string;
  merchantName?: string;
  amount: number;
  date: string;
  category?: string;
}

interface DetectedSubscription {
  name: string;
  normalizedName: string;
  amount: number;
  amountVariance: number;
  frequency: string;
  confidence: number;
  occurrences: number;
  lastDate: string;
  transactions: Transaction[];
  estimatedMonthly: number;
  estimatedYearly: number;
}

interface CategoryTotal {
  category: string;
  icon: string;
  color: string;
  monthlyCost: number;
  yearlyCost: number;
  count: number;
}

interface SubscriptionCategory {
  value: string;
  label: string;
  icon: string;
  color: string;
}

// ── Known subscription merchants for confidence scoring ─────────────

const knownSubscriptionMerchants: string[] = [
  'netflix',
  'spotify',
  'hulu',
  'disney',
  'hbo',
  'max',
  'paramount',
  'peacock',
  'apple',
  'icloud',
  'youtube',
  'amazon prime',
  'aws',
  'github',
  'gitlab',
  'slack',
  'zoom',
  'notion',
  'figma',
  'adobe',
  'microsoft',
  'office',
  'google',
  'dropbox',
  'box',
  'heroku',
  'vercel',
  'twilio',
  'stripe',
  'squarespace',
  'wix',
  'shopify',
  'wordpress',
  'bluehost',
  'godaddy',
  'namecheap',
  'cloudflare',
  'nordvpn',
  'expressvpn',
  'surfshark',
  'hulu',
  'espn',
  'hbo',
  'max',
  'disney+',
  'apple tv+',
  'youtube premium',
  'audible',
  'kindle',
  'headspace',
  'calm',
  'peloton',
  'planet fitness',
  'la fitness',
  'equinox',
  'classpass',
  'hellofresh',
  'blue apron',
  'freshly',
  'dollar shave club',
  "harry's",
  'birchbox',
  'stitch fix',
  'ipsy',
  'playstation plus',
  'xbox game pass',
  'nintendo',
  'ea play',
  'geforce now',
  'duolingo',
  'babbel',
  'rosetta stone',
  'masterclass',
  'skillshare',
  'coursera',
  'udemy',
  'linkedin',
  'tinder',
  'bumble',
  'hinge',
  'grubhub',
  'doordash',
  'uber one',
  'instacart+',
  'walmart+',
  'target circle',
  'costco',
  "sam's club",
  'aaa',
  'siriusxm',
  'pandora',
  'tidal',
  'deezer',
  'soundcloud',
  'crunchyroll',
  'funimation',
];

// ── Subscription Categories ─────────────────────────────────────────

const subscriptionCategories: SubscriptionCategory[] = [
  { value: 'streaming', label: 'Streaming', icon: 'play', color: '#d85a30' },
  {
    value: 'software',
    label: 'Software',
    icon: 'laptop-code',
    color: '#0a52d8',
  },
  { value: 'fitness', label: 'Fitness', icon: 'dumbbell', color: '#1d9e75' },
  { value: 'food', label: 'Food & Meal', icon: 'utensils', color: '#7f77dd' },
  { value: 'gaming', label: 'Gaming', icon: 'gamepad', color: '#004751' },
  { value: 'news', label: 'News & Media', icon: 'newspaper', color: '#5dcaa5' },
  { value: 'music', label: 'Music', icon: 'music', color: '#d85a30' },
  {
    value: 'shopping',
    label: 'Shopping',
    icon: 'bag-shopping',
    color: '#0a52d8',
  },
  { value: 'utilities', label: 'Utilities', icon: 'bolt', color: '#f59e0b' },
  { value: 'other', label: 'Other', icon: 'tag', color: '#888' },
];

// ── Props ───────────────────────────────────────────────────────────

const props = defineProps<{
  transactions?: Transaction[];
}>();

// ── Store & Router ──────────────────────────────────────────────────

const store = useMyStore();
const router = useRouter();

// ── State ───────────────────────────────────────────────────────────

const subscriptions = ref<Subscription[]>([]);
const nextId = ref(1);
const addingSubscription = ref(false);
const editingIndex = ref<number | null>(null);
const form = ref<SubscriptionForm>({
  name: '',
  amount: 0,
  frequency: 'monthly',
  category: 'streaming',
  active: true,
  icon: 'play',
  color: '#d85a30',
});
const formError = ref('');
const scanning = ref(false);
const detectedSubscriptions = ref<DetectedSubscription[]>([]);
const hasScanned = ref(false);

// ── Transaction Sources ─────────────────────────────────────────────

const allTransactions = computed<Transaction[]>(() => {
  if (props.transactions && props.transactions.length > 0)
    return props.transactions;
  try {
    const storeTxns = (store as any).transactions || [];
    const plaidTxns = (store as any).plaidTransactions || [];
    return [...storeTxns, ...plaidTxns].map((t: any) => ({
      id: t.id || t.transactionID || String(Math.random()),
      name: t.merchantName || t.name || 'Unknown',
      merchantName: t.merchantName || t.name || 'Unknown',
      amount: Math.abs(t.amount || 0),
      date:
        t.date || t.transactionDate || new Date().toISOString().split('T')[0],
      category: t.category || '',
    }));
  } catch (e) {
    return [];
  }
});

const hasTransactions = computed(() => allTransactions.value.length > 0);

// ── Detection Algorithm ─────────────────────────────────────────────

function normalizeMerchantName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b(inc|llc|ltd|co|corp|limited)\b\.?/gi, '')
    .replace(/\.com$/i, '')
    .replace(/\s+\d+$/g, '')
    .replace(/[^a-z0-9\s]/gi, '')
    .trim();
}

function parseDate(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00');
}

function daysBetween(a: string, b: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round(
    (parseDate(b).getTime() - parseDate(a).getTime()) / msPerDay
  );
}

function detectFrequency(avgGap: number): string {
  if (avgGap >= 5 && avgGap <= 10) return 'weekly';
  if (avgGap >= 11 && avgGap <= 17) return 'biweekly';
  if (avgGap >= 25 && avgGap <= 35) return 'monthly';
  if (avgGap >= 80 && avgGap <= 100) return 'quarterly';
  if (avgGap >= 350 && avgGap <= 380) return 'yearly';
  // Fallback: pick closest
  const targets = [
    { freq: 'weekly', gap: 7 },
    { freq: 'biweekly', gap: 14 },
    { freq: 'monthly', gap: 30 },
    { freq: 'quarterly', gap: 90 },
    { freq: 'yearly', gap: 365 },
  ];
  let closest = targets[0];
  let minDiff = Math.abs(avgGap - closest.gap);
  for (const t of targets) {
    const diff = Math.abs(avgGap - t.gap);
    if (diff < minDiff) {
      minDiff = diff;
      closest = t;
    }
  }
  return closest.freq;
}

function isKnownMerchant(name: string): boolean {
  const normalized = normalizeMerchantName(name);
  return knownSubscriptionMerchants.some(
    (km) => normalized.includes(km) || km.includes(normalized)
  );
}

function detectSubscriptions(
  transactions: Transaction[]
): DetectedSubscription[] {
  const grouped = new Map<string, Transaction[]>();

  for (const tx of transactions) {
    const normalized = normalizeMerchantName(tx.merchantName || tx.name);
    if (!normalized) continue;
    if (!grouped.has(normalized)) {
      grouped.set(normalized, []);
    }
    grouped.get(normalized)!.push(tx);
  }

  const results: DetectedSubscription[] = [];

  for (const [normalizedName, txs] of grouped.entries()) {
    if (txs.length < 2) continue;

    const sorted = [...txs].sort(
      (a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime()
    );

    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push(daysBetween(sorted[i - 1].date, sorted[i].date));
    }

    const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    const minGap = Math.min(...gaps);
    const maxGap = Math.max(...gaps);

    const amounts = sorted.map((t) => t.amount);
    const avgAmount = amounts.reduce((s, a) => s + a, 0) / amounts.length;
    const maxVar = Math.max(...amounts.map((a) => Math.abs(a - avgAmount)));
    const variancePct = avgAmount > 0 ? (maxVar / avgAmount) * 100 : 0;

    const frequency = detectFrequency(avgGap);

    // Confidence scoring
    let confidence = 0;
    if (sorted.length >= 3) confidence += 30;
    if (variancePct < 5) confidence += 25;
    if (maxGap <= avgGap * 1.2 && avgGap > 0) confidence += 25;
    if (isKnownMerchant(sorted[0].merchantName || sorted[0].name))
      confidence += 20;

    confidence = Math.min(100, confidence);

    if (confidence < 40) continue;

    // Estimate costs
    let estimatedMonthly: number;
    let estimatedYearly: number;
    switch (frequency) {
      case 'weekly':
        estimatedMonthly = avgAmount * 4.33;
        estimatedYearly = avgAmount * 52;
        break;
      case 'biweekly':
        estimatedMonthly = avgAmount * 2.17;
        estimatedYearly = avgAmount * 26;
        break;
      case 'monthly':
        estimatedMonthly = avgAmount;
        estimatedYearly = avgAmount * 12;
        break;
      case 'quarterly':
        estimatedMonthly = avgAmount / 3;
        estimatedYearly = avgAmount * 4;
        break;
      case 'yearly':
        estimatedMonthly = avgAmount / 12;
        estimatedYearly = avgAmount;
        break;
      default:
        estimatedMonthly = avgAmount;
        estimatedYearly = avgAmount * 12;
    }

    results.push({
      name: sorted[0].merchantName || sorted[0].name,
      normalizedName,
      amount: Math.round(avgAmount * 100) / 100,
      amountVariance: Math.round(variancePct * 100) / 100,
      frequency,
      confidence: Math.round(confidence),
      occurrences: sorted.length,
      lastDate: sorted[sorted.length - 1].date,
      transactions: sorted,
      estimatedMonthly: Math.round(estimatedMonthly * 100) / 100,
      estimatedYearly: Math.round(estimatedYearly * 100) / 100,
    });
  }

  return results.sort((a, b) => b.confidence - a.confidence);
}

// ── Yearly Cost Conversion ──────────────────────────────────────────

function yearlyCost(sub: Subscription): number {
  switch (sub.frequency) {
    case 'weekly':
      return sub.amount * 52;
    case 'biweekly':
      return sub.amount * 26;
    case 'monthly':
      return sub.amount * 12;
    case 'quarterly':
      return sub.amount * 4;
    case 'yearly':
      return sub.amount;
    default:
      return sub.amount * 12;
  }
}

function monthlyCost(sub: Subscription): number {
  return yearlyCost(sub) / 12;
}

// ── Computed ────────────────────────────────────────────────────────

const sortedSubscriptions = computed(() => {
  return [...subscriptions.value].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return yearlyCost(b) - yearlyCost(a);
  });
});

const totalYearlyCost = computed(() => {
  return subscriptions.value
    .filter((s) => s.active)
    .reduce((sum, s) => sum + yearlyCost(s), 0);
});

const totalMonthlyCost = computed(() => totalYearlyCost.value / 12);

const activeCount = computed(
  () => subscriptions.value.filter((s) => s.active).length
);

const categoryBreakdown = computed((): CategoryTotal[] => {
  const map = new Map<string, CategoryTotal>();
  for (const sub of subscriptions.value) {
    if (!sub.active) continue;
    const existing = map.get(sub.category);
    const mc = monthlyCost(sub);
    const yc = yearlyCost(sub);
    if (existing) {
      existing.monthlyCost += mc;
      existing.yearlyCost += yc;
      existing.count += 1;
    } else {
      const catDef = subscriptionCategories.find(
        (c) => c.value === sub.category
      );
      map.set(sub.category, {
        category: sub.category,
        icon: catDef?.icon || 'tag',
        color: catDef?.color || '#888',
        monthlyCost: mc,
        yearlyCost: yc,
        count: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.yearlyCost - a.yearlyCost);
});

const isFormValid = computed(() => {
  return form.value.name.trim().length > 0 && form.value.amount > 0;
});

// ── CRUD Functions ──────────────────────────────────────────────────

function startAddSubscription(): void {
  addingSubscription.value = true;
  editingIndex.value = null;
  form.value = {
    name: '',
    amount: 0,
    frequency: 'monthly',
    category: 'streaming',
    active: true,
    icon: 'play',
    color: '#d85a30',
  };
  formError.value = '';
}

function cancelAddSubscription(): void {
  addingSubscription.value = false;
  editingIndex.value = null;
  formError.value = '';
}

function editSubscription(idx: number): void {
  const sub = sortedSubscriptions.value[idx];
  if (!sub) return;
  // Find the original index
  const originalIdx = subscriptions.value.findIndex((s) => s.id === sub.id);
  if (originalIdx === -1) return;

  editingIndex.value = originalIdx;
  form.value = {
    name: sub.name,
    amount: sub.amount,
    frequency: sub.frequency,
    category: sub.category,
    active: sub.active,
    icon: sub.icon,
    color: sub.color,
  };
  addingSubscription.value = true;
  formError.value = '';
}

function removeSubscription(idx: number): void {
  const sub = sortedSubscriptions.value[idx];
  if (!sub) return;
  const originalIdx = subscriptions.value.findIndex((s) => s.id === sub.id);
  if (originalIdx === -1) return;
  subscriptions.value.splice(originalIdx, 1);
}

function saveSubscription(): void {
  if (!isFormValid.value) {
    formError.value = 'Please enter a name and amount.';
    return;
  }

  if (editingIndex.value !== null) {
    const sub = subscriptions.value[editingIndex.value];
    if (sub) {
      sub.name = form.value.name.trim();
      sub.amount = form.value.amount;
      sub.frequency = form.value.frequency;
      sub.category = form.value.category;
      sub.icon = form.value.icon;
      sub.color = form.value.color;
    }
  } else {
    const newSub: Subscription = {
      id: nextId.value++,
      name: form.value.name.trim(),
      amount: form.value.amount,
      frequency: form.value.frequency,
      category: form.value.category,
      source: 'manual',
      active: true,
      icon: form.value.icon,
      color: form.value.color,
    };
    subscriptions.value.push(newSub);
  }

  addingSubscription.value = false;
  editingIndex.value = null;
  formError.value = '';
}

function toggleActive(idx: number): void {
  const sub = sortedSubscriptions.value[idx];
  if (!sub) return;
  const originalIdx = subscriptions.value.findIndex((s) => s.id === sub.id);
  if (originalIdx !== -1) {
    subscriptions.value[originalIdx].active =
      !subscriptions.value[originalIdx].active;
  }
}

// ── Detection Actions ───────────────────────────────────────────────

function runScan(): void {
  scanning.value = true;
  detectedSubscriptions.value = [];

  setTimeout(() => {
    const existingNormalized = new Set(
      subscriptions.value.map((s) => normalizeMerchantName(s.name))
    );
    const detected = detectSubscriptions(allTransactions.value).filter(
      (d) => !existingNormalized.has(d.normalizedName)
    );
    detectedSubscriptions.value = detected;
    scanning.value = false;
    hasScanned.value = true;
  }, 500);
}

function acceptDetected(detected: DetectedSubscription): void {
  // Find best matching category
  const nameLower = detected.normalizedName;
  let category = 'other';
  if (
    nameLower.includes('netflix') ||
    nameLower.includes('hulu') ||
    nameLower.includes('disney') ||
    nameLower.includes('hbo') ||
    nameLower.includes('prime') ||
    nameLower.includes('paramount') ||
    nameLower.includes('peacock') ||
    nameLower.includes('apple tv')
  ) {
    category = 'streaming';
  } else if (
    nameLower.includes('spotify') ||
    nameLower.includes('apple music') ||
    nameLower.includes('pandora') ||
    nameLower.includes('tidal') ||
    nameLower.includes('deezer') ||
    nameLower.includes('soundcloud') ||
    nameLower.includes('sirius')
  ) {
    category = 'music';
  } else if (
    nameLower.includes('github') ||
    nameLower.includes('adobe') ||
    nameLower.includes('microsoft') ||
    nameLower.includes('notion') ||
    nameLower.includes('figma') ||
    nameLower.includes('slack') ||
    nameLower.includes('zoom') ||
    nameLower.includes('aws') ||
    nameLower.includes('heroku') ||
    nameLower.includes('vercel') ||
    nameLower.includes('google') ||
    nameLower.includes('dropbox')
  ) {
    category = 'software';
  } else if (
    nameLower.includes('peloton') ||
    nameLower.includes('fitness') ||
    nameLower.includes('equinox') ||
    nameLower.includes('classpass')
  ) {
    category = 'fitness';
  } else if (
    nameLower.includes('hellofresh') ||
    nameLower.includes('blue apron') ||
    nameLower.includes('freshly') ||
    nameLower.includes('doordash') ||
    nameLower.includes('grubhub')
  ) {
    category = 'food';
  } else if (
    nameLower.includes('playstation') ||
    nameLower.includes('xbox') ||
    nameLower.includes('nintendo') ||
    nameLower.includes('ea play') ||
    nameLower.includes('geforce')
  ) {
    category = 'gaming';
  } else if (nameLower.includes('vpn') || nameLower.includes('cloudflare')) {
    category = 'utilities';
  }

  const catDef = subscriptionCategories.find((c) => c.value === category);

  const newSub: Subscription = {
    id: nextId.value++,
    name: detected.name,
    amount: detected.amount,
    frequency: detected.frequency as
      | 'weekly'
      | 'biweekly'
      | 'monthly'
      | 'quarterly'
      | 'yearly',
    category,
    source: 'detected',
    active: true,
    icon: catDef?.icon || 'tag',
    color: catDef?.color || '#888',
    nextBillingDate: detected.lastDate,
  };

  subscriptions.value.push(newSub);

  // Remove from detected
  const dIdx = detectedSubscriptions.value.findIndex(
    (d) => d.normalizedName === detected.normalizedName
  );
  if (dIdx !== -1) detectedSubscriptions.value.splice(dIdx, 1);
}

function rejectDetected(idx: number): void {
  detectedSubscriptions.value.splice(idx, 1);
}

// ── Watch ───────────────────────────────────────────────────────────

watch(
  () => form.value.category,
  (newCat) => {
    const catDef = subscriptionCategories.find((c) => c.value === newCat);
    if (catDef) {
      form.value.icon = catDef.icon;
      form.value.color = catDef.color;
    }
  }
);

// ── Utility Functions ───────────────────────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function iconStyle(color: string) {
  return {
    backgroundColor: `${color}20`,
    color,
  };
}

function categoryLabel(value: string): string {
  const cat = subscriptionCategories.find((c) => c.value === value);
  return cat?.label || value;
}

function catLabel(value: string): string {
  return categoryLabel(value);
}

function frequencyLabel(freq: string): string {
  const map: Record<string, string> = {
    weekly: 'Weekly',
    biweekly: 'Bi-weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };
  return map[freq] || freq;
}

function confidenceColor(conf: number): string {
  if (conf >= 80) return '#1d9e75';
  if (conf >= 60) return '#f59e0b';
  return '#d85a30';
}

function categoryPercentage(cost: number): number {
  if (totalYearlyCost.value === 0) return 0;
  return Math.min(100, Math.round((cost / totalYearlyCost.value) * 100));
}
</script>

<style scoped>
/* ── Container ────────────────────────────────────────────────────── */
.subscription-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ── Back Arrow ──────────────────────────────────────────────────── */
.back-arrow {
  font-size: 1.25rem;
  color: var(--color-primary);
  cursor: pointer;
  padding: 0.5rem;
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

/* ── Intro ───────────────────────────────────────────────────────── */
.intro-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: linear-gradient(135deg, var(--purple-50), var(--teal-50));
  border-radius: var(--radius-lg);
  padding: 1rem;
  margin-bottom: 1rem;
}
.intro-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.1rem;
}
.intro-icon.subscription-icon {
  background-color: #0a52d820;
  color: #0a52d8;
}
.dark-theme .intro-icon.subscription-icon {
  background-color: #0a52d830;
  color: #4a8eff;
}
.intro-text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
  margin: 0;
}
.dark-theme .intro-text {
  color: #aaa;
}

/* ── Section Header Row ──────────────────────────────────────────── */
.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.link-btn {
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}
.dark-theme .link-btn {
  color: var(--color-accent-teal);
}
.link-btn:hover {
  text-decoration: underline;
}

/* ── Empty State ─────────────────────────────────────────────────── */
.empty-subscriptions {
  text-align: center;
  padding: 2rem 1rem;
}
.empty-icon {
  font-size: 2.5rem;
  color: var(--color-accent-purple);
  margin-bottom: 0.75rem;
}
.empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.25rem;
}
.dark-theme .empty-title {
  color: #eee;
}
.empty-sub {
  font-size: 0.8rem;
  color: #777;
  margin: 0 0 1rem;
  line-height: 1.4;
}
.dark-theme .empty-sub {
  color: #999;
}

/* ── Subscription Card ───────────────────────────────────────────── */
.subscription-card {
  padding: 0.875rem;
  margin-bottom: 0.625rem;
  transition: opacity var(--transition-fast);
}
.subscription-card.inactive {
  opacity: 0.6;
}
.subscription-card-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}
.subscription-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.subscription-info {
  flex: 1;
  min-width: 0;
}
.subscription-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .subscription-name {
  color: #eee;
}
.subscription-meta {
  font-size: 0.72rem;
  color: #888;
  margin: 0.125rem 0 0;
}
.dark-theme .subscription-meta {
  color: #aaa;
}
.paused-tag {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  color: #fff;
  background-color: #d85a30;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  vertical-align: middle;
  margin-left: 4px;
}
.subscription-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}
.subscription-amount-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding-top: 0.375rem;
  border-top: 1px solid #f0f0f0;
}
.dark-theme .subscription-amount-row {
  border-top-color: #333;
}
.subscription-amount-label {
  font-size: 0.7rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex: 1;
}
.dark-theme .subscription-amount-label {
  color: #aaa;
}
.subscription-amount-value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #222;
}
.dark-theme .subscription-amount-value {
  color: #eee;
}
.subscription-yearly {
  font-size: 0.75rem;
  color: #888;
  font-weight: 500;
}
.dark-theme .subscription-yearly {
  color: #aaa;
}

/* ── Add/Edit Form ───────────────────────────────────────────────── */
.add-subscription-card {
  border: 2px solid var(--color-accent-purple);
  padding: 1rem;
  margin-top: 0.5rem;
}
.add-subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}
.add-subscription-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
}
.dark-theme .add-subscription-title {
  color: #eee;
}
.form-row {
  display: flex;
  gap: 0.75rem;
}
.flex-1 {
  flex: 1;
}
.currency-input {
  position: relative;
  display: flex;
  align-items: center;
}
.currency-symbol {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  color: #888;
  font-weight: 600;
  pointer-events: none;
  z-index: 1;
}
.currency-field {
  padding-left: 24px !important;
}
.percent-input {
  position: relative;
  display: flex;
  align-items: center;
}
.percent-symbol {
  position: absolute;
  right: 10px;
  font-size: 0.9rem;
  color: #888;
  font-weight: 600;
  pointer-events: none;
  z-index: 1;
}
.percent-field {
  padding-right: 24px !important;
}

/* ── Icon Buttons ────────────────────────────────────────────────── */
.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  border: 1px solid #e0e0e0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  color: #555;
  transition: var(--transition-fast);
}
.dark-theme .icon-btn {
  background: #2a2a2a;
  border-color: #444;
  color: #ccc;
}
.icon-btn:hover {
  background: #f5f5f5;
}
.dark-theme .icon-btn:hover {
  background: #333;
}
.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.icon-btn-danger {
  color: #c0392b;
  border-color: #f5c6cb;
}
.dark-theme .icon-btn-danger {
  color: #ff6b6b;
  border-color: #5a2a2a;
}
.icon-btn-danger:hover {
  background: #fdecea;
}
.dark-theme .icon-btn-danger:hover {
  background: #3a1a1a;
}

/* ── Form Error ──────────────────────────────────────────────────── */
.form-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #c0392b;
  background: #fdecea;
  padding: 0.5rem 0.625rem;
  border-radius: var(--radius-sm);
  margin-top: 0.5rem;
}
.dark-theme .form-error {
  color: #ff6b6b;
  background: #3a1a1a;
}
.form-warn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #b7791f;
  background: #fffbeb;
  padding: 0.5rem 0.625rem;
  border-radius: var(--radius-sm);
  margin-top: 0.5rem;
}
.dark-theme .form-warn {
  color: #fbbf24;
  background: #3a3010;
}

/* ── Summary Grid ────────────────────────────────────────────────── */
.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.summary-card {
  text-align: center;
  padding: 1rem;
}
.summary-label {
  display: block;
  font-size: 0.6rem;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}
.dark-theme .summary-label {
  color: #aaa;
}
.summary-value {
  display: block;
  font-size: 1.3rem;
  font-weight: 800;
  color: #222;
}
.dark-theme .summary-value {
  color: #eee;
}

/* ── Scanning ────────────────────────────────────────────────────── */
.scanning-card {
  text-align: center;
  padding: 1.5rem 1rem;
}
.scanning-icon {
  font-size: 1.75rem;
  color: var(--color-primary);
  margin-bottom: 0.75rem;
}
.dark-theme .scanning-icon {
  color: var(--color-accent-teal);
}
.scanning-text {
  font-size: 0.85rem;
  color: #555;
  margin: 0;
}
.dark-theme .scanning-text {
  color: #aaa;
}

/* ── Detected List ───────────────────────────────────────────────── */
.detected-list {
  margin-top: 0.5rem;
}
.detected-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  margin: 0 0 0.5rem;
}
.dark-theme .detected-count {
  color: #aaa;
}
.suggestion-card {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}
.suggestion-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.375rem;
}
.suggestion-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--purple-50);
  color: var(--color-accent-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.dark-theme .suggestion-icon-wrap {
  background: #2a1a4a;
}
.suggestion-info {
  flex: 1;
  min-width: 0;
}
.suggestion-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .suggestion-name {
  color: #eee;
}
.suggestion-meta {
  font-size: 0.68rem;
  color: #888;
  margin: 0.125rem 0 0;
}
.dark-theme .suggestion-meta {
  color: #aaa;
}
.suggestion-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.suggestion-amount-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.375rem;
}
.suggestion-avg {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-primary);
}
.dark-theme .suggestion-avg {
  color: var(--color-accent-teal);
}
.suggestion-yearly {
  font-size: 0.72rem;
  color: #888;
}
.dark-theme .suggestion-yearly {
  color: #aaa;
}

/* ── Confidence Bar ──────────────────────────────────────────────── */
.confidence-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}
.dark-theme .confidence-bar {
  background: #333;
}
.confidence-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* ── Empty Scan ──────────────────────────────────────────────────── */
.empty-scan {
  text-align: center;
  padding: 2rem 1rem;
}
.empty-scan-icon {
  font-size: 2.5rem;
  color: var(--color-accent-green);
  margin-bottom: 0.75rem;
}
.empty-scan-title {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.25rem;
}
.dark-theme .empty-scan-title {
  color: #eee;
}
.empty-scan-sub {
  font-size: 0.8rem;
  color: #777;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .empty-scan-sub {
  color: #999;
}

/* ── Upgrade Card ────────────────────────────────────────────────── */
.upgrade-card {
  text-align: center;
  padding: 1.5rem 1rem;
  background: linear-gradient(135deg, var(--purple-50), var(--teal-50));
}
.upgrade-icon {
  font-size: 2rem;
  color: var(--color-accent-purple);
  margin-bottom: 0.75rem;
}
.dark-theme .upgrade-icon {
  color: #a07bff;
}
.upgrade-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.375rem;
}
.dark-theme .upgrade-title {
  color: #eee;
}
.upgrade-sub {
  font-size: 0.78rem;
  color: #666;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .upgrade-sub {
  color: #aaa;
}

/* ── Category Breakdown ──────────────────────────────────────────── */
.category-list {
  margin-top: 0.5rem;
}
.category-card {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}
.category-card-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}
.category-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.category-info {
  flex: 1;
  min-width: 0;
}
.category-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #222;
  margin: 0;
}
.dark-theme .category-name {
  color: #eee;
}
.category-meta {
  font-size: 0.7rem;
  color: #888;
  margin: 0.125rem 0 0;
}
.dark-theme .category-meta {
  color: #aaa;
}
.category-cost {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-primary);
  flex-shrink: 0;
}
.dark-theme .category-cost {
  color: var(--color-accent-teal);
}

/* ── Category Bar ────────────────────────────────────────────────── */
.category-bar-bg {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}
.dark-theme .category-bar-bg {
  background: #333;
}
.category-bar-fill {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(
    90deg,
    var(--color-accent-purple),
    var(--color-accent-teal)
  );
  transition: width 0.3s ease;
}

/* ── Misc ────────────────────────────────────────────────────────── */
.ud-section {
  margin-bottom: 1.25rem;
}
.pointer {
  cursor: pointer;
}
</style>
